import React from 'react';
import { Box, Typography } from '@mui/material';
import { colors, size, fonts } from '../../ColorFont/color';
import BaseChip from '../../components/common/BaseChip';

/**
 * RoleCard Component
 * Modular card for role selection (Buyer/Farmer) with standard brand styling.
 */
const RoleCard = ({
    title,
    description,
    tags = [],
    icon: Icon,
    selected,
    onClick
}) => {
    return (
        <Box
            onClick={onClick}
            sx={{
                width: "100%",
                minHeight: "clamp(8rem, 15vh, 10.75rem)", // clamp height based on vh bounds 
                display: "flex",
                flexDirection: "row",
                borderRadius: "2rem", // 32px rems
                border: `0.125rem solid ${selected ? colors.primary : colors.card_border_pale}`, // 2px
                backgroundColor: selected ? colors.role_selected_bg : "transparent",
                boxShadow: selected ? colors.shadow_card : "none",
                cursor: "pointer",
                transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                position: "relative",
                px: "clamp(1rem, 2vw, 1.625rem)", // rem/width bounds
                py: "clamp(1rem, 2vh, 1.625rem)", // rem/height bounds for vertical compression
                mb: "clamp(0.5rem, 2vh, 1rem)", // prevent stacked card overflow on tiny screens
                "&:hover": { borderColor: colors.primary },
            }}
        >
            {/* Icon Container */}
            <Box
                sx={{
                    width: "clamp(2.5rem, 5vh, 3.5rem)", // rem/vh bounds
                    height: "clamp(2.5rem, 5vh, 3.5rem)",
                    borderRadius: "2rem",
                    backgroundColor: selected ? colors.icon_bg_selected : colors.icon_bg_unselected,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: "clamp(0.5rem, 2vw, 1rem)",
                    flexShrink: 0,
                    transition: "background-color 0.3s ease",
                }}
            >
                <Icon color={selected ? colors.light : colors.text} />
            </Box>

            {/* Content Area */}
            <Box sx={{ flex: 1 }}>
                <Typography
                    sx={{
                        fontSize: size.h3,
                        fontWeight: 700,
                        color: colors.text_dark,
                        letterSpacing: "-0.028rem",
                        lineHeight: 1.2,
                        fontFamily: fonts.poppins,
                        mb: 'clamp(0.1rem, 0.5vh, 0.25rem)'
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    sx={{
                        fontSize: size.small,
                        fontWeight: 400,
                        color: colors.text,
                        lineHeight: 1.3,
                        mb: 'clamp(0.5rem, 1vh, 1rem)',
                        fontFamily: fonts.poppins
                    }}
                >
                    {description}
                </Typography>

                {/* Tags Row */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: "4px", sm: "6px" } }}>
                    {tags.map((tag, idx) => (
                        <BaseChip key={idx} label={tag} active={selected} />
                    ))}
                </Box>
            </Box>

            {/* Selection Checkmark Indicator */}
            {selected && (
                <Box
                    sx={{
                        position: "absolute",
                        top: "1rem", // 16px for a consistent, professional corner inset
                        right: "1rem",
                        width: "1.3rem", // Exactly 18px for a crisp, refined look
                        height: "1.3rem",
                        borderRadius: "50%",
                        backgroundColor: colors.primary,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0px 2px 4px rgba(0,0,0,0.1)", // Subtle shadow for depth
                    }}
                >
                    <Box
                        component="svg"
                        viewBox="0 0 16 16"
                        fill="none"
                        sx={{
                            width: "16px",
                            height: "16px",
                            transform: "scale(0.7)",
                        }}
                    >
                        <path
                            d="M3 8L6.5 11L13 4.5"
                            stroke="#FFFFFF"
                            strokeWidth="3.5" // Increased stroke to keep it sharp at a smaller scale
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default RoleCard;
